import React, { useState } from 'react';
import { Button, Card, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentCollections } from '@/hooks/useDocumentCollection';
import type { DocumentCollectionRequest } from '@/services/document-collection.service';

const STATUS_COLORS: Record<string, string> = {
  Assigned: 'processing',
  'Submitted for Approval': 'gold',
  Approved: 'green',
  'Attached to Delivery Note': 'success',
  Rejected: 'error',
};

const DocumentCollectionListPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | undefined>();
  const { data: rows = [], isLoading } = useDocumentCollections(status ? { status } : undefined);

  const columns: ColumnsType<DocumentCollectionRequest> = [
    {
      title: 'Collection No',
      dataIndex: 'collection_number',
      render: (v, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/document-collections/${r.id}`)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Delivery Note',
      key: 'dn',
      render: (_, r) => r.deliveryNote?.delivery_note_number ?? '—',
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, r) => r.customer?.customer_name ?? '—',
    },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag> },
    { title: 'Priority', dataIndex: 'priority', width: 90 },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Package on DN',
      key: 'pkg',
      render: (_, r) => {
        const ps = r.deliveryNote?.document_package_status;
        if (!ps) return '—';
        return <Tag>{ps}</Tag>;
      },
    },
  ];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Document Collection"
          subtitle="Prepare approved client document packages for delivery notes"
          primaryActions={
            <Button icon={<PlusOutlined />} onClick={() => navigate('/growth/delivery-notes')}>
              From delivery notes
            </Button>
          }
        />
      }
    >
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            allowClear
            placeholder="Filter status"
            style={{ width: 220 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'Assigned', label: 'Assigned' },
              { value: 'Submitted for Approval', label: 'Submitted for Approval' },
              { value: 'Attached to Delivery Note', label: 'Attached' },
              { value: 'Rejected', label: 'Rejected' },
            ]}
          />
        </Space>
        <Table rowKey="id" loading={isLoading} columns={columns} dataSource={rows} pagination={{ pageSize: 20 }} />
      </Card>
    </WorkspaceLayout>
  );
};

export default DocumentCollectionListPage;
